import React from 'react';
import ReactDOM from 'react-dom';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import * as Sentry from '@sentry/browser';
import * as BranchSDK from 'branch-sdk';
import clevertap from 'clevertap-web-sdk';

import * as serviceWorker from './serviceWorker';

import { App } from './App';

import 'src/config/i18n';

import ErrorBoundary from './modules/components/ErrorBoundary';

if (process.env.NODE_ENV === 'development') {
    // Load API mocks for development
    // Note: only enable this locally to develop new features
    // require('./mocks/api/setupLocal');
}

if (process.env.NODE_ENV !== 'development' && process.env.REACT_APP_SENTRY_KEY) {
    Sentry.init({
        dsn: process.env.REACT_APP_SENTRY_KEY,
        release: process.env.REACT_APP_RELEASE,
        environment: process.env.REACT_APP_ENV,
    });
}

if (process.env.REACT_APP_CLEVER_TAP_ACCOUNT_ID) {
    clevertap.privacy.push({ optOut: false });
    clevertap.privacy.push({ useIP: false });
    clevertap.init(process.env.REACT_APP_CLEVER_TAP_ACCOUNT_ID, 'us1');
    clevertap.enablePersonalization = true;
    clevertap.spa = true;
}

if (process.env.REACT_APP_DDRUM_CLIENT_TOKEN) {
    const datadogCommonConfig = {
        clientToken: process.env.REACT_APP_DDRUM_CLIENT_TOKEN,
        env: process.env.REACT_APP_ENV ?? 'dev',
        service: 'member-webapp-frontend',
        silentMultipleInit: true,
        site: 'datadoghq.com',
        trackSessionAcrossSubdomains: true,
        trackInteractions: true,
        useCrossSiteSessionCookie: true,
        version: process.env.REACT_APP_VERSION,
    };

    /**
     * Datadog browser logs configuration
     * {@link https://docs.datadoghq.com/logs/log_collection/javascript/#configuration}
     */
    datadogLogs.init({
        ...datadogCommonConfig,
        forwardErrorsToLogs: true,
        forwardConsoleLogs: ['error'],
        sessionSampleRate: 100,
    });

    /**
     * Datadog browser RUM configuration
     * {@link https://docs.datadoghq.com/real_user_monitoring/browser/#configuration}
     */
    datadogRum.init({
        ...datadogCommonConfig,
        applicationId: process.env.REACT_APP_DDRUM_APP_ID!,
        sampleRate: 100,
    });
}

// this is really messy right now
// TODO: create method to handle branchio in navigation service
// and add unit tests for each link
const handleBranchio = (err, data) => {
    /**
     * BranchSDK.init callback. Redirect Branch.io deeplinks to the correct route and resource.
     */
    if (err) {
        return;
    }

    if (!data?.data_parsed && data?.data_parsed['+clicked_branch_link']) return;

    // Redirect dynamic Branch.io deeplinks to the correct resource
    if (data.data_parsed?.session) {
        if (data.data_parsed['~referring_link']?.includes('video-session')) {
            let videoSession = data?.data_parsed?.session;
            if (videoSession) {
                if (videoSession.includes('&')) {
                    videoSession = videoSession.split('&')[1];
                }
                window.location.replace(`video-session/${videoSession}`);
            }
        }
    } else if (data.data_parsed['~referring_link']?.includes('set-new-password')) {
        const uidb64 = data?.data_parsed?.id;
        const token = data?.data_parsed?.token;
        if (uidb64 && token) {
            window.location.replace(`/set-new-password/${uidb64}/${token}`);
        }
    }
    return;
};

BranchSDK.init(process.env.REACT_APP_BRANCH_KEY!, { retries: 2 }, handleBranchio);

ReactDOM.render(
    <ErrorBoundary>
        <App />
    </ErrorBoundary>,
    document.getElementById('root'),
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: http://bit.ly/CRA-PWA
serviceWorker.unregister();
